import NavigateNextIcon from "@mui/icons-material/NavigateNextRounded";
import { Box, Breadcrumbs, Link as MUILink, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import last from "lodash/last";
import Link from "next/link";
import { useRouter } from "next/router";
import * as React from "react";
import { useTemplates } from "services/template";
import CategorySelector from "../categorySelector";
import List from "./list";

type BreadcrumbType = {
  type: string;
  slug: string;
  href?: string;
  name: string;
};

type Props = {
  path: string;
  slugs: string[];
  query: any;
  breadcrumbs: BreadcrumbType[];
};

export const Container = styled("div")<{ size?: "lg" | "xl" }>(
  ({ theme, size = "lg" }) => ({
    maxWidth:
      size === "lg" ? theme.breakpoints.values.lg : theme.breakpoints.values.xl,
    marginLeft: "auto",
    marginRight: "auto",
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  })
);

export default function CustomDetail({
  path,
  slugs,
  query: initQuery,
  breadcrumbs,
}: Props): React.ReactElement {
  const router = useRouter();

  const title = React.useMemo(() => {
    const breadcrumb = last(breadcrumbs || []);
    if (!breadcrumb) {
      return "";
    }
    return (breadcrumb.type === "CATEGORY" ? "Custom " : "") + breadcrumb.name;
  }, [breadcrumbs]);

  const { data } = useTemplates(initQuery);

  const { templates, pagination } = data || { templates: [], pagination: {} };

  return (
    <Container size="xl">
      <Box sx={{ my: 2 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          <Link href={"/custom/all"} passHref>
            <MUILink color="textPrimary" underline="always">
              Products
            </MUILink>
          </Link>
          {breadcrumbs?.map((item, index) =>
            item.href ? (
              <Link key={index} href={item.href} passHref>
                <MUILink color="textPrimary" underline="always">
                  {item.name}
                </MUILink>
              </Link>
            ) : (
              <Typography key={index}>{item.name}</Typography>
            )
          )}
        </Breadcrumbs>
      </Box>

      <Typography variant="h4" component="h1" align="center" sx={{ mb: 2 }}>
        {title}
      </Typography>
      <Box sx={{ mt: 4, display: "flex" }}>
        <Box
          sx={(theme) => ({
            width: 260,
            [theme.breakpoints.down("sm")]: {
              display: "none",
            },
          })}
        >
          <CategorySelector slugs={slugs} />
        </Box>
        <Box
          sx={{
            flex: 1,
            width: 0,
            ml: {
              xs: 0,
              sm: 2,
            },
          }}
        >
          <List path={path} templates={templates} pagination={pagination} />
        </Box>
      </Box>
    </Container>
  );
}
